import { MaxElapsedEpochMsConfig } from '@configs';
import { BaseServiceOutput, ErrorCode } from '@types';

import { UrlHelper } from '../urlHelper';

export type StatusCode = 'error' | 'cancelled';

export class AppHelper {
	static isBeRedirect = false;

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	static exit = (redirectUri: string, statusCode?: StatusCode, state?: any) => {
		try {
			const stateValue =
				typeof state === 'object' && !Array.isArray(state) && state !== null
					? JSON.stringify(state).toString()
					: state;

			// build url to redirect to
			const returnUrl = UrlHelper.buildUrl(redirectUri, [
				{ key: 'status', value: statusCode },
				{ key: 'state', value: stateValue },
			]);

			window.location.replace(returnUrl);
		} catch (err) {}
	};

	// Only works for simple objects, and not complex with other nested objects inside
	static camelizeKeys = (obj: object) => {
		return Object.keys(obj).reduce(
			(result, key) => ({
				...result,
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				[key.charAt(0).toLowerCase() + key.slice(1)]: obj[key],
			}),
			{}
		);
	};

	static isCorporateClient = (corporateClientsList = '', clientId = '') => {
		/* return false when no params provided */
		if (!corporateClientsList || !clientId) {
			return false;
		}

		try {
			const clientsArray = JSON.parse(corporateClientsList.replace(/'/g, '"'));

			/* return false when corporateClientsList param is not an Array */
			if (!Array.isArray(clientsArray) || !clientsArray.length) {
				return false;
			}

			/* remove the extra language part */
			let searchTerm = clientId.substring(clientId.indexOf('.') + 1);

			/* remove the extra environment part, if applicable */
			const numberOfDots = searchTerm.split('.').length - 1;

			if (numberOfDots > 1) {
				const lastDotIndex = searchTerm.lastIndexOf('.');
				searchTerm = searchTerm.substring(0, lastDotIndex);
			}

			return clientsArray.some((item) => item.includes(searchTerm));
		} catch {
			return false;
		}
	};

	static convertCatchError = (error: unknown) => {
		let result = {};

		if (error instanceof Error) {
			result = {
				name: error.name,
				message: error.message,
			};
		} else {
			const serviceError = error as BaseServiceOutput<null, null>;
			result = {
				...serviceError.metadata,
			};
		}

		return result;
	};

	static isMSE = (clientId?: string): boolean => {
		return clientId?.includes('MSE') || false;
	};

	static getOrigin = (url = ''): string => {
		const urlObject = new URL(url);
		return urlObject.origin;
	};

	static elapsedMillisecondsSinceEpoch = (epoch: string): number => {
		const numberEpoch = Number(epoch.trim());

		if (isNaN(Number(numberEpoch))) {
			throw new Error('epoch value is not a number.');
		}

		const now = Date.now();
		return now - numberEpoch;
	};

	static isValidEpochParam = (clientId?: string, epoch?: string): boolean => {
		const isMSE = AppHelper.isMSE(clientId);

		const shouldRedirectMSE = isMSE && !epoch;

		const defaultMaxElapsedEpochMs = 30000;
		const maxElapsedEpochMs =
			!MaxElapsedEpochMsConfig || isNaN(Number(MaxElapsedEpochMsConfig))
				? defaultMaxElapsedEpochMs
				: Number(MaxElapsedEpochMsConfig);

		const shouldRedirectOthers = !!epoch && AppHelper.elapsedMillisecondsSinceEpoch(epoch) > maxElapsedEpochMs;

		return !(shouldRedirectMSE || shouldRedirectOthers);
	};

	static isSessionExpired = (errorCode?: ErrorCode | string | null) => {
		return errorCode === ErrorCode.SessionExpired || errorCode === ErrorCode.SessionExpired1;
	};

	static submitForm = (
		actionUrl: string,
		data: {
			[key: string]: string | undefined | null;
		}
	) => {
		const form = document.createElement('form');
		form.method = 'POST';
		form.action = actionUrl;
		form.style.display = 'none';

		Object.keys(data).forEach((key) => {
			const input = document.createElement('input');
			input.type = 'hidden';
			input.name = key;
			input.value = data[key] || '';
			form.appendChild(input);
		});

		document.body.appendChild(form);

		form.submit();

		document.body.removeChild(form);
	};
}
